/**
 * Configuration de l'intégration Enfact (facturation electronique PEPPOL)
 */

import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { api } from '../../services/api';
import { logger } from '../../utils/logger';

interface EnfactConfig {
  enabled: boolean;
  apiKey: string;
  companyUuid: string;
  environment: 'sandbox' | 'production';
  autoCreateInvoice: boolean;
  peppolEnabled: boolean;
}

export default function EnfactSettings() {
  const { tenant, reloadTenant } = useTenant();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Configuration Enfact depuis le tenant
  const [config, setConfig] = useState<EnfactConfig>({
    enabled: false,
    apiKey: '',
    companyUuid: '',
    environment: 'sandbox',
    autoCreateInvoice: false,
    peppolEnabled: true,
  });

  // La configuration du tenant chargée au démarrage vient de la route PUBLIQUE
  // `/tenants/slug/:slug`, d'où les secrets sont désormais retirés — ils y
  // étaient servis à tout visiteur de la boutique. Cet écran, lui, est
  // authentifié : il relit le tenant par la route protégée pour disposer de la
  // clé réelle. Sans ça, le champ arriverait vide et une sauvegarde risquerait
  // d'effacer la clé en place.
  useEffect(() => {
    void reloadTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger la configuration depuis le tenant
  useEffect(() => {
    const enfactConfig = tenant?.config?.integrations?.enfact as EnfactConfig | undefined;
    if (enfactConfig) {
      setConfig({
        enabled: enfactConfig.enabled ?? false,
        apiKey: enfactConfig.apiKey ?? '',
        companyUuid: enfactConfig.companyUuid ?? '',
        environment: enfactConfig.environment ?? 'sandbox',
        autoCreateInvoice: enfactConfig.autoCreateInvoice ?? false,
        peppolEnabled: enfactConfig.peppolEnabled ?? true,
      });
    }
  }, [tenant]);

  const handleChange = (field: keyof EnfactConfig, value: string | boolean) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setTestResult(null); // Reset test result on change
  };

  const handleSave = async () => {
    if (!tenant?.id) {
      setSaveMessage({ type: 'error', text: 'Erreur: Aucun tenant selectionne' });
      return;
    }

    // Validation
    if (config.enabled && (!config.apiKey || !config.companyUuid)) {
      setSaveMessage({ type: 'error', text: 'La cle API et l\'UUID entreprise sont requis' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      await api.updateTenantConfig(tenant.id, {
        integrations: {
          enfact: config,
        },
      });

      setSaveMessage({ type: 'success', text: 'Configuration Enfact sauvegardee !' });
      await reloadTenant();
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      logger.error('[EnfactSettings] Erreur sauvegarde:', error);
      setSaveMessage({
        type: 'error',
        text: `Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!tenant?.id) return;

    setTesting(true);
    setTestResult(null);

    try {
      // Sauvegarder d'abord la config si necessaire
      if (config.apiKey && config.companyUuid) {
        await api.updateTenantConfig(tenant.id, {
          integrations: {
            enfact: config,
          },
        });
      }

      // Tester la connexion via le client API (qui utilise VITE_API_URL)
      const result = await api.get(`/enfact/${tenant.id}/test`);
      setTestResult(result);
    } catch (error) {
      logger.error('[EnfactSettings] Erreur test:', error);
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Erreur de connexion',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header avec toggle principal */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white rounded-lg shadow flex items-center justify-center text-2xl">
            <span role="img" aria-label="facturation">{"/"}</span>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Enfact - Facturation Electronique</h3>
            <p className="text-sm text-gray-600">
              Integrez vos ventes avec Enfact pour la facturation PEPPOL (obligatoire en Belgique 2026)
            </p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={config.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
          />
          <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      {config.enabled && (
        <>
          {/* Informations API */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex gap-3">
              <span className="text-amber-600 text-xl">!</span>
              <div>
                <p className="font-medium text-amber-900">Ou trouver vos identifiants Enfact ?</p>
                <p className="text-sm text-amber-800 mt-1">
                  Connectez-vous a votre compte Enfact, allez dans{' '}
                  <strong>Parametres personnels</strong> pour trouver votre cle API et UUID entreprise.
                </p>
                <a
                  href="https://app.enfact.be"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Ouvrir Enfact
                </a>
              </div>
            </div>
          </div>

          {/* Configuration API */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Configuration API</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Environment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Environnement
                </label>
                <select
                  value={config.environment}
                  onChange={(e) => handleChange('environment', e.target.value as 'sandbox' | 'production')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="production">Production</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Utilisez Sandbox pour tester avant de passer en production
                </p>
              </div>

              {/* Company UUID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UUID Entreprise (X-COMPANY-UUID) *
                </label>
                <input
                  type="text"
                  value={config.companyUuid}
                  onChange={(e) => handleChange('companyUuid', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </div>

              {/* API Key */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cle API (X-SESSION-KEY) *
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={config.apiKey}
                    onChange={(e) => handleChange('apiKey', e.target.value)}
                    className="w-full px-4 py-2 pr-24 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="Votre cle API Enfact"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                  >
                    {showApiKey ? 'Masquer' : 'Afficher'}
                  </button>
                </div>
              </div>
            </div>

            {/* Test de connexion */}
            <div className="flex items-center gap-4 pt-2">
              <button
                onClick={handleTestConnection}
                disabled={testing || !config.apiKey || !config.companyUuid}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors"
              >
                {testing ? 'Test en cours...' : 'Tester la connexion'}
              </button>
              {testResult && (
                <div
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    testResult.success
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {testResult.success ? 'Connexion OK' : testResult.message}
                </div>
              )}
            </div>
          </div>

          <hr />

          {/* Options de facturation */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Options de facturation</h4>

            <div className="space-y-3">
              {/* PEPPOL */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Envoi PEPPOL automatique</p>
                  <p className="text-sm text-gray-500">
                    Envoyer les factures via le reseau PEPPOL si le client est enregistre
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={config.peppolEnabled}
                    onChange={(e) => handleChange('peppolEnabled', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {/* Info PEPPOL */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex gap-3">
                <span className="text-blue-600 text-xl">i</span>
                <div>
                  <p className="font-medium text-blue-900">A propos de PEPPOL</p>
                  <p className="text-sm text-blue-800 mt-1">
                    PEPPOL est le reseau europeen de facturation electronique.
                    A partir de janvier 2026, toutes les entreprises belges devront
                    pouvoir envoyer et recevoir des factures via PEPPOL pour les transactions B2B.
                  </p>
                </div>
              </div>
            </div>

            {/* Workflow info */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex gap-3">
                <span className="text-emerald-600 text-xl">?</span>
                <div>
                  <p className="font-medium text-emerald-900">Comment creer une facture ?</p>
                  <ol className="text-sm text-emerald-800 mt-2 space-y-1 list-decimal list-inside">
                    <li>Dans le POS, associez un client a la vente</li>
                    <li>Cochez "Demander une facture" avant le paiement</li>
                    <li>Le ticket affiche "A FACTURER"</li>
                    <li>Allez dans <strong>Ventes → Factures Enfact</strong> pour creer la facture</li>
                  </ol>
                  <a
                    href="/invoices"
                    className="inline-block mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors"
                  >
                    Gerer les factures
                  </a>
                </div>
              </div>
            </div>
          </div>

          <hr />

          {/* Synchronisation des clients */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Synchronisation des clients</h4>

            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex gap-3">
                <span className="text-purple-600 text-xl">*</span>
                <div>
                  <p className="font-medium text-purple-900">Synchronisation automatique Folies vers Enfact</p>
                  <p className="text-sm text-purple-800 mt-1">
                    Quand vous creez une facture pour un client, celui-ci est automatiquement
                    synchronise vers Enfact s'il n'existe pas deja (via l'action FIND-OR-CREATE-CUSTOMER).
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex gap-3">
                <span className="text-gray-500 text-xl">i</span>
                <div>
                  <p className="font-medium text-gray-700">Import depuis Enfact non disponible</p>
                  <p className="text-sm text-gray-600 mt-1">
                    L'API Enfact v5 ne permet pas de lister les clients existants.
                    Vous pouvez creer vos clients directement dans Folies - ils seront
                    automatiquement synchronises vers Enfact lors de la premiere facture.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bouton de sauvegarde */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t-2 border-gray-200">
        {saveMessage && (
          <div
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              saveMessage.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}
          >
            {saveMessage.type === 'success' ? 'OK ' : '! '}
            {saveMessage.text}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  );
}
